import React, { useState, useEffect, useRef, type CSSProperties } from 'react';
import Image from 'next/image';
import type { HeroContent } from '@/data/siteContent';

export default function HomeSection({
  hero,
  setActiveSection,
}: {
  hero: HeroContent;
  setActiveSection: (section: string) => void;
}) {
  const motionOrder = (order: number) =>
    ({
      '--motion-order': order,
    }) as CSSProperties;

  const [imageSrc, setImageSrc] = useState(hero.profileImageUrl);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [titleText, setTitleText] = useState('');
  const [subtitleText, setSubtitleText] = useState('');
  const [authorText, setAuthorText] = useState('');
  const [showTitleCursor, setShowTitleCursor] = useState(true);
  const [showSubtitleCursor, setShowSubtitleCursor] = useState(false);
   
  const timeouts = useRef<ReturnType<typeof setTimeout>[]>([]);
  const intervals = useRef<ReturnType<typeof setInterval>[]>([]);

  useEffect(() => {
    return () => {
      timeouts.current.forEach(clearTimeout);
      intervals.current.forEach(clearInterval);
    };
  }, []);

  useEffect(() => {
    const fullTitle1 = hero.introLine;
    const fullTitle2 = hero.finalLine;
    const initialSubtitle = hero.subtitle;
    const quotes = hero.rotatingQuotes;

    let quoteIndex = 0;

    const type = (setter: React.Dispatch<React.SetStateAction<string>>, text: string, speed: number, onComplete?: () => void) => {
        let i = 0;
        const interval = setInterval(() => {
            setter(text.substring(0, i + 1));
            i++;
            if (i > text.length) {
                clearInterval(interval);
                if (onComplete) onComplete();
            }
        }, speed);
        intervals.current.push(interval);
    };

    const erase = (setter: React.Dispatch<React.SetStateAction<string>>, speed: number, onComplete?: () => void) => {
        const interval = setInterval(() => {
            setter(prev => {
                if (prev.length > 0) return prev.slice(0, -1);
                else { clearInterval(interval); if(onComplete) onComplete(); return ""; }
            });
        }, speed);
        intervals.current.push(interval);
    };

    const cycleQuotes = () => {
        setAuthorText("");
        setShowSubtitleCursor(true);
        const { quote, author } = quotes[quoteIndex];
        type(setSubtitleText, `"${quote}"`, 50, () => {
            setShowSubtitleCursor(false);
            timeouts.current.push(setTimeout(() => {
                type(setAuthorText, `- ${author}`, 70, () => {
                    timeouts.current.push(setTimeout(() => {
                        setShowSubtitleCursor(true);
                        erase(setSubtitleText, 30, () => {
                            erase(setAuthorText, 30, () => {
                                quoteIndex = (quoteIndex + 1) % quotes.length;
                                cycleQuotes();
                            });
                        });
                    }, 4000));
                });
            }, 500));
        });
    };

    const initialSequence = () => {
        setShowTitleCursor(true);
        type(setTitleText, fullTitle1, 120, () => {
            timeouts.current.push(setTimeout(() => {
                setShowTitleCursor(true);
                erase(setTitleText, 70, () => {
                    timeouts.current.push(setTimeout(() => {
                        let tempTitle = '';
                        let i = 0;
                        const typingInterval2 = setInterval(() => {
                            tempTitle += fullTitle2[i];
                            setTitleText(tempTitle);
                            i++;
                            if (i >= fullTitle2.length) {
                                clearInterval(typingInterval2);
                                setShowTitleCursor(false);
                                timeouts.current.push(setTimeout(() => {
                                    type(setSubtitleText, initialSubtitle, 50, () => {
                                        timeouts.current.push(setTimeout(cycleQuotes, 5000));
                                    });
                                }, 500));
                            }
                        }, 120);
                        intervals.current.push(typingInterval2);
                    }, 500));
                });
            }, 1500));
        });
    };

    initialSequence();
  }, [hero]);

  useEffect(() => {
    setImageSrc(hero.profileImageUrl);
    setImageLoaded(false);
  }, [hero.profileImageUrl]);
   
  return (
    <section id="home" className="p-6 transition-all duration-1000 ease-out snap-start flex flex-col items-center justify-center text-center min-h-[calc(100vh-4rem)] md:min-h-screen">
      <div className="hero-stage motion-card-surface rounded-2xl bg-black/60 p-8">
        <div className="hero-fragment relative mb-8 mx-auto w-fit" style={motionOrder(0)}>
            <div className="relative overflow-hidden rounded-[2rem] border border-green-900/40 bg-[radial-gradient(circle_at_top_left,rgba(34,197,94,0.16),transparent_28%),linear-gradient(180deg,rgba(3,8,5,0.96),rgba(0,0,0,0.9))] p-6 shadow-[0_25px_80px_rgba(0,0,0,0.35)]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(34,197,94,0.14),transparent_30%)]"></div>
                <div className="relative w-40 h-40">
                    <Image 
                        src={imageSrc} 
                        alt={hero.profileImageAlt} 
                        width={160}
                        height={160}
                        priority
                        className={`rounded-full w-full h-full object-cover shadow-2xl shadow-black/45 transform hover:scale-105 transition-all duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => {
                            setImageSrc('/icon.png');
                            setImageLoaded(true);
                        }}
                    />
                </div>
            </div>
        </div>
        <h1
          className="hero-fragment text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-green-600 min-h-[84px] md:min-h-[150px]"
          style={motionOrder(1)}
        >
            <span>{titleText}</span>{showTitleCursor && <span className="typing-cursor"></span>}
        </h1>
        <p
          className="hero-fragment text-base sm:text-lg md:text-xl lg:text-2xl text-gray-400 mb-6 max-w-3xl leading-relaxed min-h-[96px]"
          style={motionOrder(2)}
        >
          <span>{subtitleText}</span>{showSubtitleCursor && <span className="typing-cursor"></span>}
        </p>
         <p
           className="hero-fragment text-sm md:text-base text-green-400 mb-10 min-h-[24px] italic"
           style={motionOrder(3)}
         >
           {authorText}
         </p>
        <div
          className="hero-fragment flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 justify-center"
          style={motionOrder(4)}
        >
          <button
            onClick={() => setActiveSection(hero.primaryButtonTarget)}
            className="motion-cta px-10 py-4 bg-green-800 text-white font-semibold rounded-full shadow-lg hover:bg-green-900"
          >
            <span>{hero.primaryButtonLabel}</span>
          </button> 
          <button
            onClick={() => setActiveSection(hero.secondaryButtonTarget)}
            className="motion-cta px-10 py-4 bg-black text-gray-100 border border-gray-700 font-semibold rounded-full hover:bg-gray-900"
          >
            <span>{hero.secondaryButtonLabel}</span>
          </button> 
        </div>
      </div>
    </section>
  );
}
